import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { IMAGES } from "@/data/site";
import { Reveal } from "@/components/Motion";
import { PageHero, CTABand } from "@/components/UI";
import SEO from "@/components/SEO";

/**
 * OurStory, long-form editorial narrative of how Consilium Pro was built.
 * 5 chapters + intro frame + closing manifesto. Ends by introducing each
 * founding Principal and the Council-Built-on-Trust framing that ties the
 * firm's Latin name to the multidisciplinary team behind it.
 */
export default function OurStory() {
  return (
    <>
      <SEO
        title="Our Story"
        description="Consilium Pro Workforce Solutions did not begin with a business plan. It began with a Career Counselor, a national mission, and a series of experiences that revealed something important about organizations, and the people trusted to build the firm alongside its founder."
        path="/our-story"
        image={IMAGES.diverseTeam}
      />

      <PageHero
        eyebrow="Our Story"
        title={["Consilium Pro", "began before", <span key="k" className="text-gold">we knew it.</span>]}
        subtitle="It did not begin with a business plan, a company name, or even the intention to create a consulting firm. It began with a Career Counselor, a national mission, and a series of experiences that revealed something important about organizations."
        image={IMAGES.diverseTeam}
      />

      {/* Introduction, before Chapter 01 */}
      <section className="bg-white py-24 md:py-32" data-testid="story-intro">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8 space-y-6 text-navy/85 text-[17px] leading-[1.75]">
          <Reveal>
            <p className="text-[10px] font-700 uppercase tracking-[0.3em] text-gold">Introduction</p>
            <h2 className="mt-4 font-display font-800 text-navy text-3xl md:text-5xl leading-[1.1] tracking-tightest">
              A mission becomes stronger when people, systems, and leadership are connected.
            </h2>
          </Reveal>
          <p>
            A mission becomes stronger when people are supported by capable leaders, useful systems, proper training, and clear information. When those elements are disconnected, even the best people can struggle.
          </p>
          <p>
            At the time, <strong className="text-navy">Kentavious "Ken" Patman</strong> did not know that these lessons would eventually lead him to create Consilium Pro Workforce Solutions. He was simply doing the work in front of him and trying to leave every organization and community better than he found it.
          </p>
          <p className="text-navy font-display font-700 text-xl md:text-2xl leading-snug pt-2">
            Only later did the pattern become clear.
          </p>
        </div>
      </section>

      {/* Chapter 1, Origin */}
      <Chapter
        num="01"
        eyebrow="The Origin"
        title="A Career Counselor during one of the hardest periods for the force"
        variant="alt"
        testid="story-chapter-1"
      >
        <p>
          Looking back, the earliest foundation of Consilium Pro was formed when Ken became a Career Counselor with the Georgia Army National Guard during one of the most challenging periods for military recruitment and retention.
        </p>
        <p>
          His path into Human Resources was intentional. Ken made the deliberate decision to change his Military Occupational Specialty from <strong className="text-navy">Military Police to Human Resources Specialist (42A)</strong>, a move toward the profession that would eventually define much of his career.
        </p>
        <p>
          Approximately <strong className="text-navy">eleven days after he completed 42A School</strong>, the COVID-19 pandemic fundamentally changed the workforce environment. The timing was not planned, but his preparation placed him where he was needed.
        </p>
        <p>
          During the pandemic, the military faced a difficult reality. People were leaving, recruitment was declining, and many Soldiers no longer felt fulfilled or certain about their futures.
        </p>
        <p>
          As a Human Resources Specialist and Career Counselor, Ken worked with service members as they made some of the most important decisions of their military careers. He processed retention and retirement actions, monitored attrition, taught courses, conducted benefits briefings, and met individually with Soldiers to help them understand their options.
        </p>
        <p>
          His goal was never simply to persuade people to remain in uniform. He wanted to understand each Soldier, identify what mattered to that person, and help the individual make an informed decision about what should come next.
        </p>
        <p>
          For some Soldiers, the right decision was to continue serving. Ken helped them understand opportunities such as bonuses, student loan repayment programs, the Montgomery GI Bill Kicker, professional development, and other benefits they had earned but did not always know how to use.
        </p>
        <p>
          For others, the right decision was to leave the military. Ken gave those Soldiers the same level of attention and respect. He helped them understand Department of Veterans Affairs benefits, the VA home loan program, educational opportunities, entrepreneurship resources, and other programs that could help them build successful lives after military service.
        </p>
        <PullQuote>
          Retention was not simply about persuading someone to stay. It was about helping that person see a future, providing accurate information, and treating the person&apos;s goals with dignity.
        </PullQuote>
        <p>
          During a historically difficult period for military recruiting and retention, Ken became the first Career Counselor in decades within his organization to meet the assigned retention mission. He ultimately exceeded that mission by more than <strong className="text-navy">15 percent</strong>.
        </p>
        <p>
          That experience established one of the earliest principles behind Consilium Pro. When people receive useful information, genuine support, and a meaningful path forward, both the individual and the organization become stronger.
        </p>
      </Chapter>

      {/* Chapter 2, Building systems */}
      <Chapter
        num="02"
        eyebrow="Building Systems Around People"
        title="Turning good intentions into real infrastructure"
        testid="story-chapter-2"
      >
        <p>
          The next chapter of the story unfolded at a nonprofit organization, where Ken became its first and only recruiter.
        </p>
        <p>
          There was no established recruiting infrastructure for him to inherit. He had to build the organization&apos;s applicant tracking ecosystem and create the structure through which candidates and internal leaders experienced recruitment.
        </p>
        <p>
          That role taught him another lesson that would eventually become part of the Consilium Pro model. Helping people requires more than good intentions. Organizations need systems that are clear, consistent, efficient, and designed around the people who use them.
        </p>
        <p>
          Ken later received an unexpected opportunity to become a Human Resources Manager. That position eventually developed into an HR Director role. While there, he built the company&apos;s first formal Human Resources department from the ground up.
        </p>
        <p>
          He developed policies and an employee handbook. He administered payroll, recruited and retained employees, monitored attrition, addressed compliance requirements, and established the foundational processes the company needed to grow.
        </p>
        <p>
          His work helped prepare the organization for an acquisition. He later managed his first acquisition from the Human Resources perspective.
        </p>
        <PullQuote>
          Enter an organization, understand what is missing, build the necessary infrastructure, and help its people and operations move forward together.
        </PullQuote>
        <p>
          What began as a career in military personnel services had become something much broader. Ken was learning how to read an organization as an operating system, identify the parts that were quietly failing, and develop practical ways to repair them.
        </p>
        <p>
          The lesson was becoming increasingly clear. Good people cannot consistently overcome unclear expectations, disconnected leadership, inadequate training, or broken systems. Sustainable improvement requires an organization to strengthen those elements together.
        </p>
      </Chapter>

      {/* Chapter 3, Kosovo */}
      <Chapter
        num="03"
        eyebrow="Service Beyond the Organization"
        title="Kosovo, civil affairs, and the meaning of lasting impact"
        variant="alt"
        testid="story-chapter-3"
      >
        <p>
          Ken&apos;s leadership journey later took him to Kosovo, where he deployed alongside three people who would eventually help him build Consilium Pro: <strong className="text-navy">Jeremy Cortez, Darien Ford, and Brashad Rouland</strong>.
        </p>
        <p>
          At the time, Consilium Pro did not exist. There was no company name, formal business plan, or expectation that they would one day become Principals of the same firm. They were simply serving together, observing one another under real conditions, and developing the kind of trust that can only be built through shared responsibility and experience.
        </p>
        <p>
          During the deployment, Ken served as the <strong className="text-navy">Brigade S9 Noncommissioned Officer in Charge for Regional Command East</strong>. Within the NATO structure, the position was recognized as the Civil-Military Cooperation Deputy Chief. Within the United States military, he served as the Civil Affairs NCOIC.
        </p>
        <p>
          The mission focused on building capacity throughout Kosovo, developing partnerships, strengthening relationships between military and civilian institutions, and leaving communities better resourced than they had been before the deployment.
        </p>
        <p>
          The experience also allowed Ken to serve beside Jeremy, Darien, and Brashad in a complex international environment. Their relationships had begun years earlier in different places, but Kosovo strengthened the professional trust among them. Ken was able to observe how they adapted, contributed, solved problems, supported others, and responded when the mission demanded more of them. None of them knew it at the time, but the deployment was helping establish part of the leadership foundation that would eventually support Consilium Pro.
        </p>
        <p>
          To support a mission of that size, Ken and his Officer in Charge created a <strong className="text-navy">volunteer database using military personnel from throughout the region</strong>. The database allowed the Brigade to quickly connect willing Soldiers with community needs and organize projects across the entire Regional Command East area of responsibility. It reflected the same instinct that would later shape Consilium Pro: mission-driven work needs infrastructure. It cannot depend entirely on improvisation.
        </p>
        <div className="my-12 grid md:grid-cols-3 gap-4">
          <ProjectCard eyebrow="Paralympic Support" title="Kosovo&apos;s Paralympic wheelchair" body="Coordinated equipment procurement to support Kosovo&apos;s participation in Paralympic competition." />
          <ProjectCard eyebrow="School Heating" title="Wood-burning stoves, northern Kosovo" body="Provided and installed a heating solution for a school without adequate winter infrastructure." />
          <ProjectCard eyebrow="Search & Rescue Partnership" title="Kosovo Search & Rescue Association" body="Historic training partnership on Camp Bondsteel with equipment procurement and medical instruction." />
          <ProjectCard eyebrow="Language Capacity" title="English literacy for Kosovo Soldiers" body="Recruited military volunteers to teach Kosovo Soldiers to read, write, and interpret English, a durable interoperability skill." />
          <ProjectCard eyebrow="Community Restoration" title="Daycare classrooms restored" body="Volunteers repainted primary daycare classrooms with cartoon characters, a more welcoming environment for the children who used the rooms daily." />
          <ProjectCard eyebrow="Volunteer Infrastructure" title="RC-East Volunteer Database" body="Built with the OIC, a roster that matched military volunteers to community projects across the entire region." />
        </div>
        <p>
          Every initiative, the Paralympic support, English classes, school heating, search and rescue partnership, and daycare restoration, was made possible through volunteers coordinated using the same database and guided by the same sense of purpose. The result was more than a collection of individual projects. It was a repeatable system for connecting people, skills, resources, and community needs.
        </p>

        {/* KSRA field photography */}
        <div className="my-14">
          <p className="text-[10px] font-700 uppercase tracking-[0.25em] text-gold mb-3">In the Field</p>
          <h3 className="font-display font-700 text-navy text-2xl md:text-3xl leading-tight mb-8">
            The Kosovo Search & Rescue Partnership
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="story-ksra-gallery">
            <KSRAPhoto
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/dj1e9yas_IMG_9734.webp"
              alt="Signed KSRA flag ceremony with U.S. Army and Kosovo Search & Rescue Association members"
              caption="Signed flag ceremony, U.S. Soldiers and KSRA members after joint training"
              testid="ksra-photo-flag"
            />
            <KSRAPhoto
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/v84q9muf_IMG_9623.webp"
              alt="Ken Patman with KSRA rescuer and U.S. Army officer beside a Blackhawk helicopter"
              caption="Field exercise, Ken with a KSRA rescuer and the KFOR officer supporting the mission"
              testid="ksra-photo-blackhawk"
            />
            <KSRAPhoto
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/m5jourps_IMG_9612.webp"
              alt="Group photo with Kosovo, KSRA and U.S. flags outside a Crash Fire Rescue bay"
              caption="Partnership day, Kosovo, KSRA, and U.S. flags flown together outside Crash Fire Rescue"
              testid="ksra-photo-flags-usa"
            />
            <KSRAPhoto
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/izduq30f_IMG_9610.webp"
              alt="KSRA members holding certificates of completion outside the RC-East 148th BSB building"
              caption="Certificate of completion, KSRA members after medical & rescue training on Camp Bondsteel"
              testid="ksra-photo-certificates"
            />
          </div>
          <p className="mt-6 text-navy/55 text-xs leading-relaxed italic">
            Photography from multiple KSRA training and partnership events on Camp Bondsteel, Regional Command East, Kosovo.
          </p>
        </div>

        {/* Capstone banquet photo, breaks out of the editorial column */}
        <figure className="my-16 -mx-5 sm:mx-0 md:-ml-[calc(33%+2rem)] md:mr-0" data-testid="story-ksra-banquet">
          <div className="relative bg-navy overflow-hidden">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/pxv3mtpu_IMG_9823.webp"
              alt="KFOR 33 farewell dinner with nearly the entire Kosovo Search & Rescue Association, 2024"
              loading="lazy"
              className="w-full h-auto"
            />
            <div className="absolute top-4 left-4 md:top-6 md:left-6">
              <span className="inline-block bg-gold text-navy text-[10px] font-800 uppercase tracking-[0.2em] px-3 py-1.5">
                2024 · KFOR 33 Farewell
              </span>
            </div>
          </div>
          <figcaption className="mt-4 text-navy/70 text-sm leading-relaxed max-w-2xl">
            <strong className="text-navy font-700">The full association.</strong> At the end of the KFOR 33 rotation in 2024, nearly the entire Kosovo Search & Rescue Association gathered to recognize the work completed during our deployment, the closing image of a partnership built training by training, project by project.
          </figcaption>
        </figure>

        <PullQuote>
          The best solutions are not delivered to people from a distance. They are developed with people, grounded in their circumstances, and built to continue serving them after the engagement ends.
        </PullQuote>
        <p>
          Sustainable impact happens when organizations stop treating people as recipients of solutions and begin treating them as partners in creating those solutions.
        </p>
      </Chapter>

      {/* Chapter 4, Bringing lessons together */}
      <Chapter
        num="04"
        eyebrow="Bringing the Lessons Together"
        title="Chief People Officer & a pattern too clear to ignore"
        testid="story-chapter-4"
      >
        <p>
          After returning from Kosovo, Ken accepted an opportunity to lead Human Resources for a national security-services organization.
        </p>
        <p>
          He was later appointed <strong className="text-navy">Chief People Officer</strong>. He became the first person to hold that position in the company&apos;s history and the first Black man to lead Human Resources at the executive level during the organization&apos;s more than 30 years of operation.
        </p>
        <p>
          In that role, Ken rebuilt the Human Resources function, strengthened workforce systems, expanded preventive training, standardized administrative processes, and brought Human Resources, safety, risk, and operations into closer alignment. The improvements generated substantial savings while helping reduce workplace incidents and strengthen organizational performance throughout the United States.
        </p>
        <p>
          By that point, a consistent pattern had emerged across every chapter of his career.
        </p>
        <p>
          Ken had helped Soldiers identify their paths forward. He had created recruiting and Human Resources functions where none previously existed. He had supported international organizations and communities through complex civil and military partnerships. He had rebuilt workforce, safety, and administrative systems on a national scale.
        </p>
        <p>
          The industries, organizations, and missions were different, but the underlying challenges were often the same.
        </p>
        <PullQuote>
          Organizations do not usually struggle because they lack good people. They struggle because their people, leadership, systems, training, technology, and mission are not properly connected.
        </PullQuote>
        <p>
          That realization became the foundation of <strong className="text-navy">Consilium Pro Workforce Solutions</strong>.
        </p>
        <p>
          Ken began thinking seriously about the services the firm could provide and the organizations it could serve. The vision extended beyond traditional Human Resources consulting. Organizations needed a partner capable of understanding the entire operating environment, the workforce, but also the technology, training, logistics, communications, infrastructure, leadership, partnerships, and systems surrounding it.
        </p>
        <p className="text-navy font-display font-700 text-xl md:text-2xl leading-snug pt-2">
          The idea was ambitious. Ken also knew immediately that he could not build it alone. More importantly, he did not want to build it alone.
        </p>
      </Chapter>

      {/* Chapter 5, Shared mission, MASSIVELY EXPANDED */}
      <section className="bg-lightgray py-24 md:py-32" data-testid="story-chapter-5">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-8 items-baseline mb-12 md:mb-16">
              <div className="md:col-span-3">
                <span className="font-display font-800 text-gold/70 text-6xl md:text-7xl leading-none tracking-tightest">05</span>
              </div>
              <div className="md:col-span-9">
                <p className="text-[10px] font-700 uppercase tracking-[0.25em] text-gold">From One Person&apos;s Experience to a Shared Mission</p>
                <h2 className="mt-4 font-display font-800 text-navy text-3xl md:text-5xl leading-[1.1] tracking-tightest">
                  The vision began with one person, but the firm required a team.
                </h2>
              </div>
            </div>
          </Reveal>

          <div className="md:pl-[calc(25%+2rem)] max-w-[760px] space-y-6 text-navy/85 text-[17px] leading-[1.75]">
            <p>
              As the idea became clearer, Ken sat down and thought carefully about what Consilium Pro should offer, who it should serve, and what kind of firm it should become.
            </p>
            <p>
              He understood his own experience and knew what he could contribute. He also understood his limitations. No individual possesses every perspective, skill, or answer required to transform an organization.
            </p>
            <p>
              The most important question was no longer simply, <em>&ldquo;What services can I provide?&rdquo;</em>
            </p>
            <p className="text-navy font-display font-700 text-xl md:text-2xl leading-snug">
              The question became, <em className="text-gold">&ldquo;Who do I know and trust to stand beside me, deliver this work with integrity, and help bring this vision to life for the long term?&rdquo;</em>
            </p>
            <p>
              Ken thought about the people he had met throughout different stages of his life. Some had been with him since high school. Others entered his life through military service, emergency response training, contract security, client relationships, and executive leadership.
            </p>
            <p>
              He was not looking only for impressive credentials or technical knowledge. He was looking for <strong className="text-navy">character, judgment, loyalty, dependability, professionalism, and people who cared about serving others well</strong>. He also wanted people who could bring perspectives and expertise he did not have.
            </p>
            <p>
              Those relationships became the foundation of the Principal team.
            </p>
          </div>

          {/* Principal introductions */}
          <div className="mt-16 space-y-14" data-testid="story-principals">
            <PrincipalIntro
              name="Jeremy Cortez"
              meeting="High school → military service → Kosovo deployment"
              body={
                <>
                  <p>
                    Jeremy has been part of Ken&apos;s journey longer than any other Principal. They attended high school together and later joined the military together. What began as a friendship during their school years continued through military service and eventually brought them to Kosovo, where they deployed together.
                  </p>
                  <p>
                    Over time, Jeremy developed military and civilian expertise in logistics, intelligence, fleet systems, infrastructure, technical operations, and small business ownership.
                  </p>
                </>
              }
              brings="Operational perspective, how people, resources, equipment, processes, and infrastructure must work together to accomplish a mission."
              tags={["Logistics", "Intelligence", "Fleet Systems", "Infrastructure", "Small Business Ops"]}
              testid="principal-jeremy"
            />

            <PrincipalIntro
              name="Darien Ford"
              meeting="Homeland Response Force / FEMA training exercise, Alabama, 2017 → Kosovo deployment"
              body={
                <>
                  <p>
                    Ken met Darien around 2017 while attending his first Homeland Response Force and FEMA training exercise in Alabama. They met in an environment where preparation, emergency response, coordination, technology, and mission readiness came together.
                  </p>
                  <p>
                    With more than 20 years of experience in hardware, software, information technology, and technical training, Darien expanded the firm&apos;s ability to address digital infrastructure, cybersecurity, automation, information governance, systems integration, and emerging technology.
                  </p>
                </>
              }
              brings="Technology perspective, ensuring Consilium Pro would never treat technology as an afterthought. Technology as an essential part of organizational performance, security, and long-term resilience."
              tags={["Hardware & Software", "Cybersecurity", "Automation", "Information Governance", "Systems Integration"]}
              testid="principal-darien"
            />

            <PrincipalIntro
              name="Brashad Rouland"
              meeting="High Museum of Art, Atlanta, 2018 → military service → Kosovo deployment"
              body={
                <>
                  <p>
                    Ken met Brashad in 2018 at the High Museum of Art in Atlanta, Georgia. At the time, Ken served as Associate Director of Training and Development within a contract security operation. Their relationship began in a professional environment that required leadership, public engagement, sound judgment, adaptability, and service.
                  </p>
                  <p>
                    Brashad brought nearly 15 years of military service, small business experience, and a natural ability to build meaningful relationships. He understood that trust creates opportunity and that strong partnerships are necessary for sustainable growth.
                  </p>
                </>
              }
              brings="Business development, strategic partnerships, community engagement, and commercial growth, meaningful growth comes from credibility, service, trust, and relationships."
              tags={["Business Development", "Strategic Partnerships", "Community Engagement", "Commercial Growth"]}
              testid="principal-brashad"
            />

            <PrincipalIntro
              name="Hadiya Rucker"
              meeting="Contract security industry, early 2021, through client engagement"
              body={
                <>
                  <p>
                    Ken met Hadiya in early 2021 through his work in the contract security industry. At the time, she represented one of the organizations his team served. Their professional relationship gave Ken the opportunity to observe Hadiya&apos;s creativity, professionalism, communication style, and ability to represent an organization effectively.
                  </p>
                  <p>
                    Hadiya understood that good work must do more than create results. It must also be communicated clearly, presented thoughtfully, and connected to the people it is intended to reach.
                  </p>
                </>
              }
              brings="Public affairs, brand strategy, communications, content, stakeholder engagement, marketing, and organizational reputation, the voice and identity of Consilium Pro."
              tags={["Public Affairs", "Brand Strategy", "Communications", "Stakeholder Engagement", "Content"]}
              testid="principal-hadiya"
            />
          </div>

          {/* "The Answer Was Already Yes" callout */}
          <div className="mt-20 md:mt-24 bg-navy text-white p-10 md:p-14 relative overflow-hidden" data-testid="story-yes-callout">
            <div className="grain absolute inset-0 opacity-30" />
            <div className="relative max-w-3xl">
              <p className="text-[10px] font-700 uppercase tracking-[0.3em] text-gold">The Answer Was Already Yes</p>
              <h3 className="mt-4 font-display font-800 text-white text-3xl md:text-5xl leading-[1.1] tracking-tightest">
                Before Ken could finish presenting the idea, the answer was already yes.
              </h3>
              <div className="mt-8 space-y-5 text-white/85 text-[16px] leading-[1.75]">
                <p>
                  Ken approached each person individually. He prepared to explain the idea, describe the services, share the long-term vision, and make the case for why each person belonged within it. He expected to have to persuade them.
                </p>
                <p>
                  Instead, something unexpected happened. In nearly every conversation, before Ken could finish presenting the idea, the answer was already yes. The wording was not always exactly the same, but the message was remarkably consistent.
                </p>
                <p className="font-display font-700 text-gold text-xl md:text-2xl leading-snug py-2 border-l-2 border-gold pl-5">
                  &ldquo;Yes. I will do it because you are you.&rdquo;
                </p>
                <p>
                  The Principals were not committing to a proven company with a long history of revenue and success. They were committing to an idea and to the person asking them to help build it.
                </p>
                <p>
                  That moment also created a responsibility. Their trust meant that Consilium Pro had to become worthy of the confidence they placed in its Founder and in one another. It had to become a firm built with integrity, guided by purpose, and capable of creating something meaningful for the organizations and people it served.
                </p>
                <p className="text-white font-display font-700 text-lg md:text-xl leading-snug pt-2">
                  Consilium Pro was never simply Ken&apos;s next professional venture. It was a shared mission.
                </p>
              </div>
            </div>
          </div>

          {/* "A Council Built on Trust", Consilium etymology */}
          <div className="mt-20 md:mt-24 grid md:grid-cols-12 gap-10 items-start" data-testid="story-council">
            <div className="md:col-span-5">
              <p className="text-[10px] font-700 uppercase tracking-[0.3em] text-gold">A Council Built on Trust</p>
              <h3 className="mt-4 font-display font-800 text-navy text-3xl md:text-4xl leading-[1.1] tracking-tightest">
                The right people, united around a common mission, can solve what no individual can solve alone.
              </h3>
              <div className="mt-8 p-8 bg-white border-t-2 border-gold">
                <p className="text-[10px] font-700 uppercase tracking-wider text-gold">The Name</p>
                <p className="mt-3 font-display font-800 text-navy text-4xl tracking-tightest leading-none">Consilium</p>
                <p className="mt-3 text-navy/60 text-xs uppercase tracking-wider">Latin</p>
                <p className="mt-4 text-navy/85 text-sm leading-relaxed">
                  Counsel, deliberation, wise advice, and the gathering of judgment around important decisions.
                </p>
              </div>
            </div>

            <div className="md:col-span-7 space-y-5 text-navy/85 text-[16px] leading-[1.75]">
              <p>
                Each relationship began in a different place. Jeremy&apos;s began in high school and continued through military service. Darien&apos;s began during emergency response training in Alabama. Brashad&apos;s began at the High Museum of Art in Atlanta.
              </p>
              <p>
                Years later, Ken, Jeremy, Darien, and Brashad deployed to Kosovo together. That shared experience strengthened relationships that would eventually become part of the foundation of Consilium Pro. Hadiya&apos;s relationship with Ken began through a professional client engagement in the contract security industry.
              </p>
              <p>
                At the time these relationships began, none of them knew they were meeting future Principals. Consilium Pro had not yet been imagined. Still, every shared experience allowed Ken to observe how these individuals worked, led, solved problems, treated people, and responded when a mission demanded more of them.
              </p>
              <p>
                By the time Ken invited them to help build the firm, his decisions were not based on assumptions. They were based on years of firsthand experience and earned trust.
              </p>
              <p>
                <strong className="text-navy">The team was not formed by asking who was available. It was formed by asking who could be trusted to help build something capable of outlasting everyone involved.</strong>
              </p>
              <p>
                Each Principal brought a different discipline, but they were never intended to operate as separate consultants who happened to share a company name. They were brought together to function as one council, challenging assumptions, offering specialized judgment, supporting one another, and creating solutions that no single person or professional discipline could develop alone.
              </p>
              <p>
                Consilium Pro was created to help businesses, government entities, nonprofit organizations, and highly regulated organizations strengthen their workforces, improve operations, manage risk, and prepare for sustainable growth.
              </p>
              <p>
                We understand the responsibility that comes with advising organizations because our methods were not developed only in a classroom or boardroom. They were shaped through service, tested in complex environments, and refined by professionals who have spent their careers building systems around real people and real missions.
              </p>
              <p className="text-navy font-display font-700 text-xl leading-snug pt-2">
                The original vision grew from Ken&apos;s experiences, but Consilium Pro came to life through the people who agreed to build it beside him. This was never meant to be a one-person story. It is the story of a shared mission and the people trusted to carry it forward.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing manifesto */}
      <section className="bg-navy py-28 md:py-40 relative overflow-hidden" data-testid="story-closing">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gold/40" />
        <div className="relative max-w-[1000px] mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-[10px] font-700 uppercase tracking-[0.3em] text-gold">The Purpose</p>
            <h2 className="mt-6 font-display font-800 text-white text-4xl md:text-6xl leading-[1.05] tracking-tightest">
              Consilium Pro began <br /><em className="text-gold not-italic">before we knew it.</em>
            </h2>
            <p className="mt-10 text-white/75 text-lg leading-relaxed max-w-2xl mx-auto">
              Today, it continues with a clear purpose.
            </p>
            <ul className="mt-10 space-y-4 max-w-xl mx-auto text-left">
              {[
                "Understand the mission.",
                "Strengthen the people.",
                "Improve the system.",
                "Build something that lasts.",
              ].map((line, i) => (
                <motion.li
                  key={line}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6 }}
                  className="flex items-baseline gap-4"
                >
                  <span className="font-display font-800 text-gold text-2xl w-8">0{i + 1}</span>
                  <span className="font-display font-700 text-white text-xl md:text-2xl">{line}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-16 pt-10 border-t border-white/15 max-w-md mx-auto">
              <p className="font-display font-700 text-gold text-lg md:text-xl tracking-wider">
                Strategic people, stronger teams, lasting impact.
              </p>
            </div>
            <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/team"
                data-testid="story-cta-team"
                className="inline-flex items-center gap-2 bg-gold text-navy font-700 px-8 py-4 hover:bg-white transition-colors"
              >
                Meet the Principals <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="story-cta-contact"
                className="inline-flex items-center gap-2 bg-transparent text-white border border-white/40 font-700 px-8 py-4 hover:bg-white hover:text-navy transition-colors"
              >
                Start the conversation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}

/* -------------------- Sub-components -------------------- */

function Chapter({ num, eyebrow, title, children, variant = "default", testid }) {
  const isAlt = variant === "alt";
  return (
    <section
      className={isAlt ? "bg-lightgray py-24 md:py-32" : "bg-white py-24 md:py-32"}
      data-testid={testid}
    >
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 items-baseline mb-12 md:mb-16">
            <div className="md:col-span-3">
              <span className="font-display font-800 text-gold/70 text-6xl md:text-7xl leading-none tracking-tightest">
                {num}
              </span>
            </div>
            <div className="md:col-span-9">
              <p className="text-[10px] font-700 uppercase tracking-[0.25em] text-gold">{eyebrow}</p>
              <h2 className="mt-4 font-display font-800 text-navy text-3xl md:text-5xl leading-[1.1] tracking-tightest">
                {title}
              </h2>
            </div>
          </div>
        </Reveal>

        <div className="md:pl-[calc(25%+2rem)] max-w-[720px] space-y-6 text-navy/85 text-[17px] leading-[1.75]">
          {children}
        </div>
      </div>
    </section>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote className="my-10 md:my-12 border-l-4 border-gold pl-6 md:pl-8 py-2">
      <Quote className="w-6 h-6 text-gold/60 mb-3" strokeWidth={1.5} />
      <p className="font-display font-700 text-navy text-xl md:text-2xl leading-[1.4] tracking-tight">
        {children}
      </p>
    </blockquote>
  );
}

function ProjectCard({ eyebrow, title, body }) {
  return (
    <div className="bg-white border border-slate-200 p-6 border-t-2 border-t-gold">
      <p className="text-[10px] font-700 uppercase tracking-wider text-gold">{eyebrow}</p>
      <h4 className="mt-3 font-display font-700 text-navy text-base leading-snug">{title}</h4>
      <p className="mt-3 text-navy/70 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function KSRAPhoto({ src, alt, caption, testid }) {
  return (
    <figure className="group relative overflow-hidden bg-navy" data-testid={testid}>
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]"
        />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/95 via-navy/70 to-transparent p-4 pt-16">
        <p className="text-[11px] leading-snug text-white/95 font-500">{caption}</p>
      </div>
    </figure>
  );
}

function PrincipalIntro({ name, meeting, body, brings, tags, testid }) {
  return (
    <Reveal>
      <div className="bg-white border-l-4 border-gold p-8 md:p-10 grid md:grid-cols-12 gap-6" data-testid={testid}>
        <div className="md:col-span-4">
          <p className="text-[10px] font-700 uppercase tracking-[0.25em] text-gold">Principal</p>
          <h3 className="mt-2 font-display font-800 text-navy text-2xl md:text-3xl leading-tight tracking-tightest">
            {name}
          </h3>
          <p className="mt-3 text-navy/55 text-xs uppercase tracking-wider leading-relaxed">
            First met: {meeting}
          </p>
          <div className="mt-5 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="text-[10px] font-600 text-navy/70 border border-slate-300 px-2 py-1 uppercase tracking-wider">
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="md:col-span-8 space-y-4 text-navy/85 text-[15px] leading-[1.7]">
          {body}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <p className="text-[10px] font-700 uppercase tracking-wider text-gold mb-2">Brings to the firm</p>
            <p className="text-sm text-navy leading-relaxed">{brings}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
